import * as React from 'react'
import { Calendar as CalendarIcon } from 'lucide-react'
import { PopoverContent, PopoverTrigger, Popover } from '../UI/popover'
import FbButton from '../FbUI/FbButton'
import { cn } from '../UI/cn'
import { Calendar } from '../UI/calendar'

interface DatePickerProps {
  value?: string
  onChange: (value?: string) => void
  label?: string
  optional?: boolean
  className?: string
  /** Enable typing in the input field. Default: false (button-only mode) */
  allowTyping?: boolean
}

function formatYmd(date: Date): string {
  // Extract local date components to preserve the calendar date selected by user
  // This ensures clicking Dec 5 stores "2024-12-05" regardless of timezone
  // (react-day-picker provides dates in local time, not UTC)
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function parseYmd(value?: string): Date | undefined {
  if (!value) return undefined
  const [y, m, d] = value.split('-').map((v) => parseInt(v, 10))
  if (!y || !m || !d) return undefined

  // Create date at midnight local time to match how react-day-picker works
  // This ensures consistency between stored dates and calendar display
  const dt = new Date(y, m - 1, d, 0, 0, 0, 0)
  if (Number.isNaN(dt.getTime())) return undefined
  return dt
}

/**
 * Parse MM/DD/YYYY format into YYYY-MM-DD
 */
function parseInputDate(input: string): string | undefined {
  if (!input.trim()) return undefined

  // Only accept MM/DD/YYYY format
  const match = input.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (match) {
    const [, m, d, y] = match
    const month = parseInt(m)
    const day = parseInt(d)
    const year = parseInt(y)

    // Validate month and day ranges
    if (month < 1 || month > 12) return undefined
    if (day < 1 || day > 31) return undefined

    // Create date at midnight local time to match parseYmd behavior
    const date = new Date(year, month - 1, day, 0, 0, 0, 0)
    if (!isNaN(date.getTime())) return formatYmd(date)
  }

  return undefined
}

/**
 * Auto-format input as MM/DD/YYYY while typing
 */
function formatInputWithSlashes(value: string, prevValue: string): string {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '')

  // Limit to 8 digits (MMDDYYYY)
  const limited = digits.slice(0, 8)

  // Check if user is deleting
  const isDeleting = value.length < prevValue.length

  if (isDeleting) {
    // When deleting, just return the value with non-digits removed but keep slashes where appropriate
    if (limited.length <= 2) {
      return limited
    } else if (limited.length <= 4) {
      return `${limited.slice(0, 2)}/${limited.slice(2)}`
    } else {
      return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`
    }
  }

  // Auto-add slashes while typing
  if (limited.length <= 2) {
    return limited
  } else if (limited.length <= 4) {
    return `${limited.slice(0, 2)}/${limited.slice(2)}`
  } else {
    return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`
  }
}

function formatDisplayDate(value?: string): string {
  if (!value) return ''
  const date = parseYmd(value)
  if (!date) return value
  // Format as MM/DD/YYYY for display using local date components
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  const y = date.getFullYear()
  return `${m}/${d}/${y}`
}

export function DatePicker({
  value,
  onChange,
  label,
  optional,
  className,
  allowTyping = false,
}: DatePickerProps) {
  const selected = React.useMemo(() => parseYmd(value), [value])
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState(formatDisplayDate(value))
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [isFocused, setIsFocused] = React.useState(false)

  // Sync input value when external value changes
  React.useEffect(() => {
    setInputValue(formatDisplayDate(value))
  }, [value])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (allowTyping) {
      const formatted = formatInputWithSlashes(e.target.value, inputValue)
      setInputValue(formatted)
    }
  }

  const handleInputBlur = () => {
    if (!allowTyping) return

    if (!inputValue.trim()) {
      onChange(undefined)
      return
    }

    const parsed = parseInputDate(inputValue)
    if (parsed) {
      onChange(parsed)
      setInputValue(formatDisplayDate(parsed))
    } else {
      // Reset to previous valid value if parsing fails
      setInputValue(formatDisplayDate(value))
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (allowTyping && e.key === 'Enter') {
      e.preventDefault()
      handleInputBlur()
    }
  }

  const hasValue = !!inputValue.trim()

  const handleContainerClick = () => {
    if (allowTyping) {
      inputRef.current?.focus()
    }
    setOpen(true)
  }

  const handleFocus = () => {
    setIsFocused(true)
    if (allowTyping) {
      setOpen(true)
    }
  }

  const handleBlur = () => {
    setIsFocused(false)
    if (allowTyping) {
      handleInputBlur()
    }
  }

  // Determine border and background styles based on state (for allowTyping mode)
  const getContainerStyles = () => {
    if (isFocused || open) {
      // Hover/Focus state: background #FAFAFA, border #087443
      return 'bg-[#FAFAFA] !border-new-company-green-active'
    }
    if (hasValue) {
      // Filled state: background white, border #06512F
      return 'bg-white !border-new-company-green-dark'
    }
    // Empty state: background white, border #E2E2E2
    return 'bg-white !border-[#E2E2E2]'
  }

  // Typeable input mode
  if (allowTyping) {
    return (
      <div className={cn('flex flex-col gap-2 w-full', className)}>
        {label && (
          <label className="text-[12px] font-medium leading-[20px] text-[#191D23] select-none">
            {label}
            {optional && (
              <span className="text-[12px] italic font-normal">
                {' '}
                - optional
              </span>
            )}
          </label>
        )}
        <div className="relative w-full">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <div
                className={cn(
                  'relative flex flex-row items-center px-3 h-10 border rounded-lg shadow-[0px_1px_2px_rgba(16,24,40,0.05)] cursor-text transition-colors',
                  getContainerStyles()
                )}
                onClick={handleContainerClick}
              >
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={handleInputChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown}
                  placeholder="MM/DD/YYYY"
                  className={cn(
                    'w-full h-6 pr-8 text-base leading-6 bg-transparent border-none outline-none',
                    hasValue || isFocused
                      ? 'text-[#191D23]'
                      : 'text-[#C4C4C4] placeholder:text-[#C4C4C4]'
                  )}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleContainerClick()
                  }}
                />
                <CalendarIcon className="absolute right-3 h-4 w-4 text-[#C4C4C4] pointer-events-none" />
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <div className="bg-white">
                <Calendar
                  key={`calendar-${selected?.getTime() || 'no-date'}`}
                  initialFocus={false}
                  mode="single"
                  selected={selected}
                  defaultMonth={selected || new Date()}
                  onSelect={(dt) => {
                    const newValue = dt ? formatYmd(dt) : undefined
                    onChange(newValue)
                    setInputValue(formatDisplayDate(newValue))
                    setOpen(false)
                  }}
                  numberOfMonths={1}
                />
                <div className="flex items-center justify-between px-4 pb-3">
                  <button
                    type="button"
                    className="text-new-company-green-active hover:opacity-90 text-sm font-medium"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      onChange(undefined)
                      setInputValue('')
                      setOpen(false)
                    }}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="text-new-company-green-active hover:opacity-90 text-sm font-medium"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      const today = formatYmd(new Date())
                      onChange(today)
                      setInputValue(formatDisplayDate(today))
                      setOpen(false)
                    }}
                  >
                    Today
                  </button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    )
  }

  // Button-only mode (original behavior)
  return (
    <div className={cn('flex flex-col mt-1 w-full', className)}>
      {label && (
        <label className="text-base font-medium text-gray-800 select-none mb-2">
          {label}
          {optional && (
            <span className="text-sm italic font-normal"> - optional</span>
          )}
        </label>
      )}
      <div className="grid gap-2 w-full">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <FbButton
              variant="secondary"
              className={cn(
                'w-full justify-start text-left font-normal hover:text-black',
                !selected && 'text-muted-foreground'
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {selected ? (
                selected.toLocaleDateString()
              ) : (
                <span>Pick a date</span>
              )}
            </FbButton>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <div className="bg-white">
              <Calendar
                key={`calendar-${selected?.getTime() || 'no-date'}`}
                initialFocus
                mode="single"
                selected={selected}
                defaultMonth={selected || new Date()}
                onSelect={(dt) => {
                  onChange(dt ? formatYmd(dt) : undefined)
                  setOpen(false)
                }}
                numberOfMonths={1}
              />
              <div className="flex items-center justify-between px-4 pb-3">
                <button
                  type="button"
                  className="text-new-company-green-active hover:opacity-90 text-sm font-medium"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onChange('')
                    setOpen(false)
                  }}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="text-new-company-green-active hover:opacity-90 text-sm font-medium"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onChange(formatYmd(new Date()))
                    setOpen(false)
                  }}
                >
                  Today
                </button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}

export default DatePicker
